import { colorsPalettes, mixins } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";
import { SearchInput } from "@similarweb/ui-components";
import React from "react";
import styled, { css } from "styled-components";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { PrimaryBoxTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { i18nFilter } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { DownloadExcelContainer, SearchContainer } from "app/pages/workspace/StyledComponent";
import { SubTitleReferrals } from "app/pages/website-analysis/incoming-traffic/StyledComponents";
import { CategoryItem } from "app/components/React/CategoriesDropdown/CategoryDropdown";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { buildFiltersForTable } from "./IncomingTrafficTableUtils";
import type { SelectorHandlers } from "app/components/filters-bar/useSelectorTrigger";
import DurationService from "app/services/DurationService";
import CountryService from "app/services/CountryService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";

interface ISource {
    count: number;
    id: string;
    text: string;
}

interface IIncomingTrafficDesktopTableTopProps extends SelectorHandlers {
    allCategories: Array<{
        id: string;
        text: string;
        formattedText: string;
        icon: string;
        count: number;
        children: Array<{
            id: string;
            text: string;
            formattedText: string;
            count: number;
        }>;
    }>;
    allSourceTypes: ISource[];
    onSelectSourceType: (value: string) => void;
    selectedSourceType: string;
    onFilterChange: (filters: { filter: string }, option: boolean) => void;
    selectedCategory?: any;
    searchTerm?: string;
    excelLink: string;
    tableColumns: any[];
    onClickToggleColumns: (index) => void;
    filtersStateObject?: {
        filter: string;
        webSource: string;
    };
    downloadExcelPermitted?: boolean;
    totalCount: number;
}

const Container = styled.div`
    display: flex;
    align-items: center;

    ${SearchContainer} {
        flex-grow: 1;
    }
`;

const Right = styled.div`
    flex-grow: 0;
    display: flex;
    align-items: center;
    margin-left: 10px;
`;

const CategoryItemContainer = styled(CategoryItem)`
    font-weight: 400;
`;

const Section = styled.div<{ margin?: number }>`
    padding: 0 12px;
    display: flex;
    align-items: center;
    ${({ margin }) =>
        margin &&
        css`
            margin: ${margin}px 0;
        `};
`;

const ChipdownItem = styled.div`
    flex-grow: 0;
    margin-right: 8px;
`;

const Title = styled.div`
    padding: 24px 24px 22px 24px;
    ${mixins.setFont({ $size: 20, $color: colorsPalettes.carbon[500], $weight: 500 })};
`;

const Separator = styled.hr`
    border-top-color: ${colorsPalettes.carbon[50]};
    margin: 0;
`;

const SectionContainer = styled.div`
    padding: 0 24px;
`;

const i18n = i18nFilter();

interface ISubTitleProps extends SelectorHandlers {
    country: number;
    duration: string;
    webSource: string;
}

const SubTitle = ({
    country,
    duration,
    webSource,
    onDateClick,
    onCountryClick,
    onDateHover,
    onCountryHover,
    onWebSourceHover,
}: ISubTitleProps) => {
    const durationObject = DurationService.getDurationData(duration);
    const is28d = duration === "28d";
    const [fromDate, toDate] = [durationObject.raw.from, durationObject.raw.to];
    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: fromDate.valueOf(),
                to: toDate.valueOf(),
                useRangeDisplay: !is28d && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
            },
            onClick: onDateClick,
            onMouseEnter: onDateHover,
        },
        {
            filter: "country",
            countryCode: country,
            value: CountryService.getCountryById(country).text,
            onClick: onCountryClick,
            onMouseEnter: onCountryHover,
        },
        {
            filter: "webSource",
            value: webSource,
            onMouseEnter: onWebSourceHover,
        },
    ];

    return <BoxSubtitle filters={subTitleFilters} />;
};

export class IncomingTrafficDesktopTableTop extends React.PureComponent<IIncomingTrafficDesktopTableTopProps, any> {
    public static defaultProps = {
        allCategories: [],
        allSourceTypes: [],
        selectedSourceType: "",
    };

    private swNavigator = proNavigator;

    constructor(props) {
        super(props);
        this.state = {
            shouldClearValue: false,
        };
    }

    public render() {
        const { shouldClearValue } = this.state;
        const { country, duration, webSource, search, tableCategory } = this.swNavigator.getParams();
        const isPaidExcelPermitted = isPaidExcelAllowed();
        const excelDownloadUrl = this.props.downloadExcelPermitted ? this.props.excelLink : "";
        let excelLinkHref = {};
        if (excelDownloadUrl !== "") {
            excelLinkHref = { href: excelDownloadUrl };
        }
        return (
            <div>
                <Title>
                    <PrimaryBoxTitle tooltip={i18n("analysis.source.referrals.table.desktop.title.tooltip")}>
                        {i18n("analysis.source.referrals.table.desktop.title")}
                    </PrimaryBoxTitle>
                    <SubTitleReferrals>
                        <SubTitle
                            country={country}
                            duration={duration}
                            webSource={webSource}
                            onDateClick={this.props.onDateClick}
                            onCountryClick={this.props.onCountryClick}
                            onDateHover={this.props.onDateHover}
                            onCountryHover={this.props.onCountryHover}
                            onWebSourceHover={this.props.onWebSourceHover}
                        />
                    </SubTitleReferrals>
                </Title>
                <Separator />
                <SectionContainer>
                    <Section margin={19}>
                        <>
                            <ChipdownItem>
                                <ChipDownContainer
                                    width={340}
                                    onClick={this.onSelectCategory}
                                    selectedText={
                                        this.props.selectedCategory
                                            ? this.props.selectedCategory.replace(/~/g, " > ").replace(/_/g, " ")
                                            : undefined
                                    }
                                    onCloseItem={this.onClearCategory}
                                    buttonText={i18n("analysis.source.referrals.table.filter.categories")}
                                    searchPlaceHolder={i18n(
                                        "analysis.source.referrals.table.filter.categories.searchCategory",
                                    )}
                                    noResultsMessage={i18n("dropdown.no.results")}
                                    hasSearch={true}
                                >
                                    {this.getCategoriesOptions()}
                                </ChipDownContainer>
                            </ChipdownItem>
                            <ChipdownItem>
                                <ChipDownContainer
                                    width={340}
                                    onClick={this.onSelectSourceType}
                                    selectedText={this.props.selectedSourceType ?? ""}
                                    onCloseItem={this.onClearSourceType}
                                    buttonText={i18n("analysis.source.referrals.table.filter.sources")}
                                    searchPlaceHolder={i18n(
                                        "analysis.source.referrals.table.filter.sources.searchSource",
                                    )}
                                    noResultsMessage={i18n("dropdown.no.results")}
                                    tooltipDisabled={true}
                                    hasSearch={true}
                                >
                                    {this.getSourceTypesOptions()}
                                </ChipDownContainer>
                            </ChipdownItem>
                        </>
                        <Button
                            type="flat"
                            isDisabled={this.isClearAllDisabled()}
                            onClick={this.onClearAll}
                        >
                            {i18n("forms.buttons.clearall.text")}
                        </Button>
                    </Section>
                </SectionContainer>
                <Container>
                    <SearchContainer>
                        <SearchInput
                            clearValue={shouldClearValue}
                            defaultValue={search}
                            debounce={400}
                            onChange={this.onSearch}
                            placeholder={i18n("forms.search.placeholder")}
                        />
                        <Right>
                            <FlexRow>
                                <DownloadExcelContainer>
                                    <DownloadButtonMenu
                                        Excel={true}
                                        downloadUrl={excelDownloadUrl}
                                        exportFunction={this.trackExcelDownload}
                                        excelLocked={!this.props.downloadExcelPermitted}
                                        PaidExcel={isPaidExcelPermitted}
                                        totalCount={this.props.totalCount}
                                    />
                                </DownloadExcelContainer>
                                <div>
                                    <ColumnsPickerLite
                                        {...this.getColumnsPickerLiteProps()}
                                        withTooltip={true}
                                        translations={{
                                            tooltip: i18n("workspace.sales.column.picker"),
                                            getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                                i18n("common.columnsPicker.selectedAmount", {
                                                    selected: selectedValue,
                                                    total: totalValue,
                                                }),
                                            mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                                            apply: i18n("common.apply"),
                                        }}
                                    />
                                </div>
                            </FlexRow>
                        </Right>
                    </SearchContainer>
                </Container>
            </div>
        );
    }

    private getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = this.props.tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle: this.onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };

    private onColumnToggle = (key) => {
        if (this.props.tableColumns[key].visible) {
            allTrackers.trackEvent("Drop Down", "remove", `Table Columns/Change`);
        } else {
            allTrackers.trackEvent("Drop Down", "add", `Table Columns/Change`);
        }
        this.props.onClickToggleColumns(parseInt(key));
    };

    private getSourceTypesOptions = () => {
        const items = this.props.allSourceTypes;
        return items.map((source: ISource): React.ReactNode => {
            const { count, id, text } = source;
            return (
                <EllipsisDropdownItem
                    id={id}
                    key={id}
                    text={text}
                    infoText={count}
                >
                    {text}
                </EllipsisDropdownItem>
            );
        });
    };

    private convertCategory = ({ count: Count, text: Name, children: Sons = [], id, icon = undefined }) => {
        const text = `${Name}${Count ? ` (${Count})` : ``}`;
        return {
            text,
            id,
            isCustomCategory: false,
            isChild: Sons.length === 0,
            icon,
            forApi: id,
        };
    };

    private getCategoriesOptions = () => {
        const categories = this.props.allCategories;
        const items = categories.reduce((result, category) => {
            if (category.children.length > 0) {
                return [
                    ...result,
                    this.convertCategory(category),
                    ...category.children.map((son) => this.convertCategory(son)),
                ];
            } else {
                return [...result, this.convertCategory(category)];
            }
        }, []);
        return items.map((item, index) => {
            return (
                <CategoryItemContainer
                    {...item}
                    key={index}
                    selected={item.forApi === this.props.selectedCategory}
                />
            );
        });
    };

    private onSelectSourceType = (source): void => {
        const value = source ? source.id : "Clear Filter";
        allTrackers.trackEvent("Drop Down", "click", `Table/Traffic Source/${value}`);
        const filters = buildFiltersForTable({
            sourceType: source && source.id,
            search: this.props.searchTerm,
            category: this.props.selectedCategory ?? "",
        });
        this.props.onFilterChange(filters, false);
        this.swNavigator.updateQueryParams(
            {
                sourceType: source && source.id,
            },
            { reload: false },
        );
    };
    private onClearSourceType = (): void => {
        this.onSelectSourceType(null);
    };

    private onSearch = (search) => {
        allTrackers.trackEvent("Search Bar", "click", `Table/${search}`);
        const filters = buildFiltersForTable({
            sourceType: this.props.selectedSourceType,
            search: search,
            category: this.props.selectedCategory ?? "",
        });
        this.props.onFilterChange(filters, false);
        this.setState({
            searchInput: search,
        });
        this.swNavigator.updateQueryParams(
            {
                sourceType: this.props.selectedSourceType,
                search: search || " ",
                tableCategory: this.props.selectedCategory,
            },
            {
                reload: false,
                notify: false,
            },
        );
    };

    private onSelectCategory = (category) => {
        const value = category
            ? category.text // category.forApi.replace("~", " > ").replace("_", " ")
            : "Clear Filter";
        allTrackers.trackEvent("Drop Down", "click", `Table/Referring Category/${value}`);

        const filters = buildFiltersForTable({
            sourceType: this.props.selectedSourceType,
            search: this.props.searchTerm,
            category: category?.forApi ?? "",
        });

        this.props.onFilterChange(filters, true);
        this.swNavigator.updateQueryParams(
            {
                tableCategory: category?.forApi || "no-category",
            },
            { reload: false },
        );
    };

    private onClearCategory = () => {
        this.onSelectCategory(null);
    };

    private isClearAllDisabled = () => {
        // button is disabled unless one of the filters has selected value
        return !(this.props.selectedCategory || this.props.selectedSourceType || this.props.searchTerm);
    };

    private onClearAll = () => {
        const filters = buildFiltersForTable({
            sourceType: "",
            search: "",
            category: "",
        });
        this.props.onFilterChange(filters, false);
        this.swNavigator.updateQueryParams(
            {
                tableCategory: null,
                search: null,
                sourceType: null,
            },
            { reload: false },
        );
        this.setState({
            shouldClearValue: true,
        });
        setTimeout(() => {
            this.setState({ shouldClearValue: false });
        });
        allTrackers.trackEvent("Button", "click", "Table/Clear all");
    };

    private trackExcelDownload = () => {
        allTrackers.trackEvent("Download", "submit-ok", "Table/Excel");
    };
}
